import { apiClient } from '@/common/lib/api-client';

const remindersBase = (companyId) =>
  `/v1/leads/companies/${companyId}/reminders`;

/**
 * GET /v1/leads/companies/{company_id}/reminders/
 */
export async function getReminders(companyId, params = {}) {
  const { data } = await apiClient.get(`${remindersBase(companyId)}/`, {
    params,
  });
  return data;
}

/**
 * GET /v1/leads/companies/{company_id}/reminders/summary/
 */
export async function getRemindersSummary(companyId, params = {}) {
  const { data } = await apiClient.get(`${remindersBase(companyId)}/summary/`, {
    params,
  });
  return data;
}

/**
 * GET /v1/leads/companies/{company_id}/reminders/{reminder_id}/
 */
export async function getReminderDetail({ companyId, reminderId }) {
  const { data } = await apiClient.get(
    `${remindersBase(companyId)}/${reminderId}/`,
  );
  return data;
}

/**
 * @typedef {Object} ImportColumn
 * @property {string} header
 * @property {string} [key]
 * @property {boolean} [required]
 * @property {string} [description]
 * @property {*} [example]
 */

/**
 * GET /v1/leads/companies/{company_id}/reminders/import/schema/
 * Returns column list + sample rows. Frontend builds the XLSX locally.
 */
export async function getReminderImportSchema({
  companyId,
  templateId,
  templateName,
  channel,
  sendingMode,
}) {
  const params = {};
  if (channel) params.channel = channel;
  if (sendingMode) params.sending_mode = sendingMode;
  if (templateId) {
    params.template_id = templateId;
  } else if (templateName) {
    params.template_name = templateName;
  }
  if (!params.template_id && !params.template_name && channel !== 'email') {
    throw new Error('template_id or template_name is required.');
  }

  const { data } = await apiClient.get(
    `${remindersBase(companyId)}/import/schema/`,
    { params },
  );
  return data;
}

/**
 * GET /v1/leads/companies/{company_id}/reminders/import/sample-template/
 * Fallback only when client-side XLSX generation fails.
 */
export async function getReminderImportSampleTemplate({
  companyId,
  templateName,
  templateVariables,
  sendingMode,
}) {
  const params = {};
  if (templateName) params.template_name = templateName;
  if (sendingMode) params.sending_mode = sendingMode;
  if (templateVariables?.length) {
    params.template_variables = templateVariables.join(',');
  }

  const { data } = await apiClient.get(
    `${remindersBase(companyId)}/import/sample-template/`,
    { params, responseType: 'blob' },
  );
  return data;
}

/**
 * POST /v1/leads/companies/{company_id}/reminders/import/preview/
 */
export async function previewReminderImport({ companyId, formData }) {
  const { data } = await apiClient.post(
    `${remindersBase(companyId)}/import/preview/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/**
 * POST /v1/leads/companies/{company_id}/reminders/import/confirm/
 */
export async function confirmReminderImport({ companyId, payload }) {
  const { data } = await apiClient.post(
    `${remindersBase(companyId)}/import/confirm/`,
    payload,
  );
  return data;
}

/**
 * GET /v1/leads/companies/{company_id}/reminders/import/jobs/
 */
export async function getReminderImportJobs(companyId) {
  const { data } = await apiClient.get(
    `${remindersBase(companyId)}/import/jobs/`,
  );
  return data;
}

/**
 * GET /v1/leads/companies/{company_id}/reminders/import/jobs/{job_id}/
 */
export async function getReminderImportJob({ companyId, jobId }) {
  const { data } = await apiClient.get(
    `${remindersBase(companyId)}/import/jobs/${jobId}/`,
  );
  return data;
}
